import * as React from 'react';
import { RaterApprovalRater } from './models';
import RaterApprovalTableRow from './RaterApprovalTableRow';
import { FIELDSET_DEFAULT } from '../layout/fieldSetStyle';
import { css } from '@emotion/core';
import { COLORS } from '../globalStyle/colors';
import { TextDirection } from '../localization/textDirection';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import { useWindowSize } from '../core/hooks/useWindowSize';
import RaterApprovalCard from './RaterApprovalCard';
import { Map } from '../language/models';
import { canEditRater, isApproved, isDeclined } from './module';
import LocalDiv from '../localization/LocalDiv';


const styles = {
  table:          {
    root: css`
      table-layout: auto;
      width: 100%;
      border-collapse: collapse;

      & th, & td {
        padding: 18px 4px;
        border: 1px solid;
        border-color: ${COLORS.LIGHTER_GREY} ${COLORS.LIGHTEST_GREY};

        &:first-child:dir(rtl), &:last-child:dir(ltr) {
          padding-right: 12px;
        }

        &:first-child:dir(ltr), &:last-child:dir(rtl) {
          padding-left: 12px;
        }
      }
    `,
    row:  css`
      color: ${COLORS.DARK_GREY};
      background-color: ${COLORS.LIGHTEST_GREY};
    `,
  },
  cardsContainer: css`
    margin-bottom: 72px; // Prevents the last card from being partially hidden behind the mobile toolbar
  `,
};


type Props = {
  className?: string;
  raters: RaterApprovalRater[];
  textDirection: TextDirection;

  ratersToApprove: Map<RaterApprovalRater>;
  ratersToDecline: Map<RaterApprovalRater>;

  showRaterNames: boolean;

  markRaterApproved: (rater: RaterApprovalRater) => void;
  markRaterDeclined: (rater: RaterApprovalRater) => void;
}


/**
 * RaterApprovalList: a component that presents information about the raters to be approved for a given project and
 * participant.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterApprovalList: React.FC<Props> = props => {
  const { markRaterApproved, markRaterDeclined, ...rest } = props;

  const mapRatersToRows = React.useMemo(
    () => rest.raters.map(rater => (
      <RaterApprovalTableRow
        key={rater.raterId}
        rater={rater}
        css={styles.table.row}
        textDirection={rest.textDirection}
        showName={rest.showRaterNames}
        approved={!!rest.ratersToApprove[rater.raterId] || isApproved(rater)}
        declined={!!rest.ratersToDecline[rater.raterId] || isDeclined(rater)}
        canEdit={canEditRater(rater)}
        markRaterApproved={() => markRaterApproved(rater)}
        markRaterDeclined={() => markRaterDeclined(rater)}
      />
    )),
    [
      rest.raters,
      rest.ratersToApprove,
      rest.ratersToDecline,
      rest.showRaterNames,
      rest.textDirection,
      markRaterApproved,
      markRaterDeclined,
    ],
  );

  const mapRatersToCards = React.useMemo(
    () => rest.raters.map(rater => (
      <RaterApprovalCard
        key={rater.raterId}
        rater={rater}
        css={styles.table.row}
        textDirection={rest.textDirection}
        showName={rest.showRaterNames}
        approved={!!rest.ratersToApprove[rater.raterId] || isApproved(rater)}
        declined={!!rest.ratersToDecline[rater.raterId] || isDeclined(rater)}
        canEdit={canEditRater(rater)}
        markRaterApproved={() => markRaterApproved(rater)}
        markRaterDeclined={() => markRaterDeclined(rater)}
      />
    )),
    [
      rest.raters,
      rest.ratersToApprove,
      rest.ratersToDecline,
      rest.showRaterNames,
      rest.textDirection,
      markRaterApproved,
      markRaterDeclined,
    ],
  );

  const isRtl = React.useMemo(() => rest.textDirection === TextDirection.RTL, [rest.textDirection]);

  const { isMobile } = useWindowSize();

  return (
    <fieldset css={FIELDSET_DEFAULT}>
      <form>
        {
          isMobile
            ? (
              <LocalDiv css={styles.cardsContainer}>
                {mapRatersToCards}
              </LocalDiv>
            )
            : (
              <table css={styles.table.root}>
                <thead css={css`text-align: ${isRtl ? 'right' : 'left'};`}>
                  <tr css={styles.table.row}>
                    <th>
                      <Msg str={Str.Email} />
                    </th>
                    <th>
                      <Msg str={Str.Relationship} />
                    </th>
                    <th>
                      <Msg str={Str.Approve} />
                    </th>
                    <th>
                      <Msg str={Str.Decline} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {mapRatersToRows}
                </tbody>
              </table>
            )
        }
      </form>
    </fieldset>
  );
};

export default RaterApprovalList;
